import React, { useEffect, useState } from "react";
import {
  Autocomplete, Box, Button, CircularProgress, Grid, MenuItem, Rating, Stack, TextField, Typography,
} from "@mui/material";
import RateReviewOutlinedIcon from "@mui/icons-material/RateReviewOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../../api/client";
import { endpoints } from "../../api/endpoints";
import { PageHeader, SectionCard } from "../ui";
import { extractErrorMessage } from "../../utils/errorMessage";
import { useAuth } from "../../context/AuthContext";
import { sortByLabel } from "../../utils/sortOptions";
import RichTextEditor from "../common/RichTextEditor";

const RECOMMENDATIONS = [
  { value: "promote", label: "Ready for promotion" },
  { value: "stretch", label: "Stretch assignment" },
  { value: "continue", label: "Continue in current role" },
  { value: "coach", label: "Needs coaching" },
  { value: "pip", label: "Performance improvement plan" },
];

const RATING_FIELDS = [
  { key: "workQuality", label: "Work Quality" },
  { key: "productivity", label: "Productivity" },
  { key: "communicationSkills", label: "Communication" },
  { key: "teamwork", label: "Teamwork" },
  { key: "problemSolvingSkills", label: "Problem Solving" },
  { key: "attendancePunctuality", label: "Attendance & Punctuality" },
];

export default function ManagerFeedbackFormNew() {
  const nav = useNavigate();
  const { user, hasRole } = useAuth();
  const [directReports, setDirectReports] = useState([]);

  useEffect(() => {
    const managerId = user?.employeeid || user?.employee?.employeeid;
    if (managerId) {
      api.get(endpoints.employees.directReports(managerId))
        .then((res) => setDirectReports(res.data.data || res.data || []))
        .catch(() => {});
    }
  }, []);

  const [form, setForm] = useState({
    employeeID: "",
    workQuality: 0, productivity: 0, communicationSkills: 0,
    teamwork: 0, problemSolvingSkills: 0, attendancePunctuality: 0,
    goalsSet: "", achievements: "", skillsToImprove: "", trainingNeeds: "",
    managerFeedback: "", employeeComments: "",
    overallRating: 0, recommendation: "continue",
    managerSignature: "",
  });
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const avgRating = (
    RATING_FIELDS.reduce((sum, f) => sum + (parseInt(form[f.key]) || 0), 0) / RATING_FIELDS.length
  ).toFixed(1);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.employeeID) return toast.error("Employee ID is required");
    if (!form.overallRating || form.overallRating === 0) return toast.error("Overall Rating is required");
    if (!form.managerSignature?.trim()) return toast.error("Manager signature is required");
    setLoading(true);
    try {
      await api.post("/api/feedback/managerfeedback", form);
      toast.success("Feedback submitted");
      nav("/manager-feedback");
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally { setLoading(false); }
  };

  // SR 121: employees are not allowed to submit manager feedback
  if (!hasRole("admin", "hr", "manager")) {
    return (
      <Box p={3}>
        <Typography color="error" variant="h6">Access Denied</Typography>
        <Typography>Only managers, HR, and admins can submit manager feedback.</Typography>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <PageHeader
        title="Manager Feedback"
        subtitle="Structured performance review for a direct report"
        icon={<RateReviewOutlinedIcon />}
      />

      <Box component="form" onSubmit={submit}>
        <Grid container spacing={3}>
          <Grid item xs={12} lg={8}>
            <SectionCard title="Employee" subtitle="Who is this review for?" accent="#6C50BF" sx={{ mb: 3 }}>
              <Autocomplete
                options={sortByLabel(directReports, (o) => o.name ? `${o.name} (${o.employeeid})` : o.employeeid || "")}
                getOptionLabel={(o) => o.name ? `${o.name} (${o.employeeid})` : o.employeeid || ""}
                value={directReports.find((e) => e.employeeid === form.employeeID) || null}
                onChange={(_, v) => set("employeeID", v ? v.employeeid : "")}
                freeSolo={false}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    label={<>Employee <span style={{ color: "red" }}>*</span></>}
                    placeholder="Select a direct report"
                    required
                  />
                )}
              />
            </SectionCard>

            <SectionCard title="Competency Ratings" subtitle="Rate each area 1-5" accent="#F9BC06" sx={{ mb: 3 }}>
              <Grid container spacing={2}>
                {RATING_FIELDS.map((f) => (
                  <Grid item xs={12} sm={6} key={f.key}>
                    <Box sx={{ p: 2, borderRadius: 2, border: "1px solid", borderColor: "divider" }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 0.5 }}>{f.label}</Typography>
                      <Rating
                        size="large" value={Number(form[f.key]) || 0}
                        onChange={(_, v) => set(f.key, v || 0)}
                      />
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </SectionCard>

            <SectionCard title="Narrative Feedback" accent="#39C645" sx={{ mb: 3 }}>
              <Stack spacing={2}>
                <TextField fullWidth multiline minRows={3} label="Goals set this cycle" value={form.goalsSet} onChange={(e) => set("goalsSet", e.target.value)} />
                <RichTextEditor compact minHeight={110} label="Key achievements" value={form.achievements} onChange={(html) => set("achievements", html)} />
                <RichTextEditor compact minHeight={110} label="Areas to improve" value={form.skillsToImprove} onChange={(html) => set("skillsToImprove", html)} />
                <TextField fullWidth multiline minRows={2} label="Training / development needs" value={form.trainingNeeds} onChange={(e) => set("trainingNeeds", e.target.value)} />
                <RichTextEditor compact minHeight={110} label="Manager feedback" value={form.managerFeedback} onChange={(html) => set("managerFeedback", html)} placeholder="Overall commentary, strengths and development areas…" />
                <TextField fullWidth multiline minRows={3} label="Employee comments (if any)" value={form.employeeComments} onChange={(e) => set("employeeComments", e.target.value)} />
              </Stack>
            </SectionCard>
          </Grid>

          <Grid item xs={12} lg={4}>
            <Box sx={{ position: { lg: "sticky" }, top: { lg: 88 } }}>
              <SectionCard title="Summary" accent="#6C50BF">
                <Stack spacing={2}>
                  <Box textAlign="center" sx={{ p: 2, borderRadius: 3, bgcolor: "#F4EDFA" }}>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: "text.secondary", letterSpacing: 0.5 }}>
                      Average Competency
                    </Typography>
                    <Typography sx={{ fontSize: 36, fontWeight: 800, color: "primary.main", lineHeight: 1.1 }}>
                      {avgRating}
                    </Typography>
                    <Rating value={Number(avgRating)} precision={0.1} readOnly size="small" />
                  </Box>

                  <Box>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 0.5 }}>
                      Overall Rating <span style={{ color: "red" }}>*</span>
                    </Typography>
                    <Rating value={Number(form.overallRating) || 0} onChange={(_, v) => set("overallRating", v || 0)} size="large" />
                  </Box>

                  <TextField select fullWidth size="small" label={<>Recommendation <span style={{ color: "red" }}>*</span></>} value={form.recommendation} onChange={(e) => set("recommendation", e.target.value)}>
                    {sortByLabel(RECOMMENDATIONS, (r) => r.label).map((r) => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
                  </TextField>

                  <TextField
                    fullWidth
                    size="small"
                    required
                    label={<>Manager Signature <span style={{ color: "red" }}>*</span></>}
                    value={form.managerSignature}
                    onChange={(e) => set("managerSignature", e.target.value)}
                    placeholder="Type your full name to sign"
                    inputProps={{ minLength: 2 }}
                  />

                  <Button
                    type="submit" variant="contained" size="large" fullWidth
                    disabled={loading || !form.employeeID || !form.managerSignature?.trim() || !form.overallRating}
                    startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SendOutlinedIcon />}
                  >
                    {loading ? "Submitting…" : "Submit Feedback"}
                  </Button>
                </Stack>
              </SectionCard>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}
